/**
 * Horizontal-only "bring this into view".
 *
 * `Element.scrollIntoView` scrolls *every* scrollable ancestor, so inside a
 * handyout it also moves the viewer's vertical snap container and drags the
 * reader back to the page the module sits on. These helpers touch exactly one
 * element — the horizontal scroller that was asked for — and never reach the
 * page scroll.
 */

export type ScrollIntoViewXAlign = "center" | "nearest";

/**
 * Names the selection a request belongs to. Two taps that resolve to the same
 * target are the same request to a memo that only sees DOM nodes, so a caller
 * that can tell them apart says so here.
 */
export type ScrollIntoViewXGeneration = string | number;

export interface ScrollIntoViewXOptions {
  align?: ScrollIntoViewXAlign;
  behavior?: ScrollBehavior;
  /** Set to false to centre the same target again on the same container. */
  once?: boolean;
  /**
   * Which selection asked for this scroll. `once` suppresses a repeat only
   * inside one generation, so re-picking a target that was centred under an
   * earlier generation scrolls again. Left out, the request is deduplicated on
   * the target and alignment alone, which cannot separate a genuine re-pick
   * from an effect that re-ran under the standing selection.
   */
  generation?: ScrollIntoViewXGeneration;
}

interface ContainerMemo {
  /**
   * Bumped on every recorded scroll, so only the target scrolled to last can
   * still match. Keyed per container alone, a target stayed marked for the
   * container's whole lifetime; carried in the mark, the previous target goes
   * stale the moment anything else is centred.
   */
  epoch: number;
  /** Weak on the target too, so a detached node drops its mark with itself. */
  marks: WeakMap<Element, string>;
}

/**
 * A caller may run inside an effect whose dependency array contains a freshly
 * built array, so it fires after *every* render -- including every render the
 * viewer triggers while the reader scrolls the page. Re-centring on each of
 * those undoes a sideways swipe the reader just made by hand, so a request
 * that repeats the one last scrolled to is skipped by default.
 */
const memos = new WeakMap<HTMLElement, ContainerMemo>();

function markFor(
  align: ScrollIntoViewXAlign,
  generation: ScrollIntoViewXGeneration | undefined,
  epoch: number,
): string {
  return `${align}\u0000${generation ?? ""}\u0000${epoch}`;
}

export function scrollIntoViewX(
  container: HTMLElement | null | undefined,
  target: Element | null | undefined,
  {
    align = "center",
    behavior = "smooth",
    once = true,
    generation,
  }: ScrollIntoViewXOptions = {},
): void {
  if (!container || !target) return;

  const memo = memos.get(container);
  if (
    once &&
    memo &&
    memo.marks.get(target) === markFor(align, generation, memo.epoch)
  ) {
    return;
  }

  const maxScrollLeft = container.scrollWidth - container.clientWidth;
  if (maxScrollLeft <= 0) return;

  const view = container.getBoundingClientRect();
  const box = target.getBoundingClientRect();

  let delta: number;
  if (align === "nearest") {
    const overflowStart = view.left - box.left;
    const overflowEnd = box.right - view.right;
    if (overflowStart <= 0 && overflowEnd <= 0) return;
    delta = overflowStart > 0 ? -overflowStart : overflowEnd;
  } else {
    delta = box.left + box.width / 2 - (view.left + view.width / 2);
  }

  const left = Math.min(
    Math.max(container.scrollLeft + delta, 0),
    maxScrollLeft,
  );
  // Only a scroll that actually happened counts as centred. Recording a target
  // the container was already showing would block the next genuine request for
  // it under `once`.
  if (Math.abs(left - container.scrollLeft) < 1) return;

  const next = memo ?? { epoch: 0, marks: new WeakMap<Element, string>() };
  next.epoch += 1;
  next.marks.set(target, markFor(align, generation, next.epoch));
  if (!memo) memos.set(container, next);

  container.scrollTo({ left, behavior });
}

export function nearestScrollerX(
  target: Element | null | undefined,
): HTMLElement | null {
  if (!target) return null;

  let node = target.parentElement;
  while (node) {
    if (node.scrollWidth > node.clientWidth + 1) {
      const overflowX = getComputedStyle(node).overflowX;
      if (overflowX === "auto" || overflowX === "scroll") {
        return node;
      }
    }
    node = node.parentElement;
  }
  return null;
}

export function scrollNearestScrollerX(
  target: Element | null | undefined,
  options?: ScrollIntoViewXOptions,
): void {
  if (!target) return;

  const scroller = nearestScrollerX(target);
  if (!scroller) return;

  scrollIntoViewX(scroller, target, options);
}
